import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import {
  createSubscriptionCheckoutSessionAction,
  createSubscriptionUpdatePortalSessionAction,
} from "../actions/restaurantActions";

const PaymentWrapper = styled.div`
  max-width: 100%;
  padding: 24px;
  border-radius: 16px;
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  font-family: "Kanit", sans-serif;
`;

const PaymentHeader = styled.div`
  font-size: 22px;
  font-weight: bold;
  color: #99b0ad;
  margin-bottom: 14px;
  text-align: center;
`;

const PaymentText = styled.p`
  font-size: 14px;
  color: #555;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.45;
`;

const SubmitButton = styled.button`
  background-color: #ee754c;
  color: white;
  font-weight: bold;
  padding: 12px 20px;
  border-radius: 999px;
  border: none;
  width: 100%;
  transition: background 0.3s;

  &:hover {
    background-color: #ee561b;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin-top: 12px;
  text-align: center;
  font-size: 14px;
`;

const SmallNote = styled.div`
  font-size: 12px;
  color: #777;
  text-align: center;
  margin-top: 12px;
  line-height: 1.35;
`;

const getCurrentReactPageUrl = () => {
  const href = window.location.href;

  // HashRouter:
  // http://localhost:3000/#/some/page?x=1
  // returns:
  // http://localhost:3000/#/some/page
  if (href.includes("#/")) {
    const [base, hashPart] = href.split("#");
    const cleanHashRoute = hashPart.split("?")[0].split("&")[0];

    return `${base}#${cleanHashRoute}`;
  }

  // BrowserRouter:
  // http://localhost:3000/some/page?x=1
  return `${window.location.origin}${window.location.pathname}`;
};

const StripeCreatePayMethod = ({
  subscription_number,
  restaurant_id,
  isUpdate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleHostedStripeRedirect = async () => {
    setErrorMessage(null);
    setIsRedirecting(true);

    try {
      const currentPageUrl = getCurrentReactPageUrl();

      const successUrl = isUpdate
        ? `${currentPageUrl}?portal_update=success`
        : `${currentPageUrl}?checkout=success&session_id={CHECKOUT_SESSION_ID}`;

      const cancelUrl = isUpdate
        ? `${currentPageUrl}?portal_update=cancelled`
        : `${currentPageUrl}?checkout=cancelled`;

      console.log("[stripe hosted flow] return urls:", {
        isUpdate,
        href: window.location.href,
        pathname: window.location.pathname,
        hash: window.location.hash,
        currentPageUrl,
        successUrl,
        cancelUrl,
      });

      const data = isUpdate
        ? await dispatch(
            createSubscriptionUpdatePortalSessionAction(
              restaurant_id,
              subscription_number,
              {
                current_page_url: currentPageUrl,
                success_url: successUrl,
                cancel_url: cancelUrl,
              },
            ),
          )
        : await dispatch(
            createSubscriptionCheckoutSessionAction(
              restaurant_id,
              subscription_number,
              {
                success_url: successUrl,
                cancel_url: cancelUrl,
              },
            ),
          );

      console.log("[stripe hosted flow] session response:", data);

      if (!data?.url) {
        throw new Error(t("ui.i18nFinalCleanup.paymentSessionNoUrl"));
      }

      window.location.href = data.url;
    } catch (err) {
      console.error("[stripe hosted flow] redirect error:", err);

      setErrorMessage(
        err?.message || t("ui.i18nFinalCleanup.couldNotOpenStripe"),
      );

      setIsRedirecting(false);
    }
  };

  return (
    <PaymentWrapper>
      <PaymentHeader>
        {isUpdate ? t("ui.i18nCleanup2.confirmarAlteracao") : t("ui.i18nCleanup2.pagamentoSeguro")}
      </PaymentHeader>

      <PaymentText>
        {isUpdate
          ? t("ui.i18nCleanup2.vaiSerRedirecionadoParaAPaginaSeguraDaStripeParaConfirmarAAlteracaoDoP")
          : t("ui.i18nCleanup2.vaiSerRedirecionadoParaAPaginaSeguraDaStripeParaConcluirOPagamentoEAAu")}
      </PaymentText>

      <SubmitButton
        type="button"
        onClick={handleHostedStripeRedirect}
        disabled={isRedirecting}
      >
        {isRedirecting
          ? t("ui.i18nCleanup2.aAbrirPaginaSegura")
          : isUpdate
            ? t("ui.i18nCleanup2.continuarParaConfirmarAlteracao")
            : t("ui.i18nCleanup2.continuarParaPagamentoSeguro")}
      </SubmitButton>

      <SmallNote>
        {isUpdate
          ? t("ui.i18nCleanup2.depoisDaConfirmacaoVoltaraAutomaticamenteAEstaPaginaParaAtualizarASubs")
          : t("ui.i18nCleanup2.depoisDoPagamentoVoltaraAutomaticamenteAEstaPaginaParaConfirmarASubscr")}
      </SmallNote>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </PaymentWrapper>
  );
};

export default StripeCreatePayMethod;
